import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { faqService, contentService, complaintService } from '@/services/api';
import { getMailHref, getMapHref, getTelHref } from '@/lib/contactLinks';
import {
  HelpCircle,
  MessageSquare,
  Phone,
  Mail,
  Search,
  FileText,
  Send,
  Loader2,
  Clock,
  MapPin,
} from 'lucide-react';

const HelpPage = () => {
  useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const defaultFaqs = useMemo(
    () => [
      {
        question: 'How do I book an appointment?',
        answer:
          'Select a doctor from the doctors list, choose a suitable date and time, and complete the booking process.',
      },
      {
        question: 'How can I cancel my appointment?',
        answer:
          'Go to the Appointments page, select the appointment you want to cancel, and click the cancel button.',
      },
      {
        question: 'What payment methods are available?',
        answer: 'We accept cash payment at the clinic, credit cards, and health insurance.',
      },
      {
        question: 'How do I add a doctor to favorites?',
        answer: 'Click the heart icon on the doctor card to add them to your favorites list.',
      },
      {
        question: 'Can I reschedule my appointment?',
        answer: 'Yes, go to the Appointments page and click reschedule to select a new time.',
      },
      {
        question: 'How do I contact my doctor?',
        answer: 'Use the Messages feature to communicate with your doctor directly through the app.',
      },
    ],
    []
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const faqResponse = await faqService.getAll();
        if (faqResponse.data?.data && Array.isArray(faqResponse.data.data)) {
          const transformedFaqs = faqResponse.data.data.map((faq) => ({
            question: faq.question_en || faq.question_ar || faq.question || '',
            answer: faq.answer_en || faq.answer_ar || faq.answer || '',
          }));
          setFaqs(transformedFaqs.length > 0 ? transformedFaqs : defaultFaqs);
        } else {
          setFaqs(defaultFaqs);
        }

        try {
          const settingsResponse = await contentService.getSettings();
          if (settingsResponse.data?.data) {
            setSettings(settingsResponse.data.data);
          }
        } catch (_) {
          // Keep fallback contact info.
        }
      } catch (error) {
        console.error('Failed to fetch FAQs:', error);
        setFaqs(defaultFaqs);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [defaultFaqs]);

  const filteredFaqs = faqs.filter((faq) => {
    const question = (faq.question || '').toLowerCase();
    const answer = (faq.answer || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return question.includes(query) || answer.includes(query);
  });
  const supportPhone = settings?.support_phone || settings?.phone_number || '+963 11 123 4567';
  const supportEmail = settings?.support_email || settings?.email || 'support@dactrha.com';
  const supportAddress = settings?.address || 'Damascus, Syria';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const body = [
        contactForm.message?.trim() || '',
        contactForm.name?.trim() ? `Name: ${contactForm.name.trim()}` : '',
        contactForm.email?.trim() ? `Email: ${contactForm.email.trim()}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      await complaintService.create({
        title: contactForm.name?.trim()
          ? `Support Request - ${contactForm.name.trim()}`
          : 'Support Request',
        body,
        message: body,
      });

      toast.success('Your message has been sent', {
        description: 'We will get back to you soon',
      });
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      const apiMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(apiMessage || 'Unable to send message, please login and try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="help-page">
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Help & Support</h1>
          </div>
          <p className="text-teal-100 max-w-xl">Search our FAQs or contact us for assistance</p>

          <div className="mt-6 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search for your question..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white focus:text-slate-900 focus:placeholder:text-slate-400"
                data-testid="search-faq"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="faq">
              <FileText className="h-4 w-4 mr-2" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="contact">
              <MessageSquare className="h-4 w-4 mr-2" />
              Contact Us
            </TabsTrigger>
          </TabsList>

          <TabsContent value="faq">
            <Card className="dark:bg-slate-800 border-0 shadow-sm max-w-3xl mx-auto">
              <CardContent className="pt-6">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-6 w-3/4" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    ))}
                  </div>
                ) : filteredFaqs.length === 0 ? (
                  <div className="text-center py-8">
                    <HelpCircle className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">No results found</p>
                  </div>
                ) : (
                  <Accordion type="single" collapsible className="w-full">
                    {filteredFaqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left hover:no-underline">{faq.question}</AccordionTrigger>
                        <AccordionContent className="text-slate-600 dark:text-slate-400">{faq.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact">
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-lg">Send us a message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={contactForm.name}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, name: e.target.value }))}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Message</Label>
                      <Textarea
                        value={contactForm.message}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, message: e.target.value }))}
                        rows={4}
                        required
                        data-testid="contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      disabled={submitting}
                      data-testid="submit-contact"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Send
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Phone className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Call Us</p>
                        <a
                          href={getTelHref(supportPhone)}
                          className="text-teal-600 transition-colors hover:text-teal-700 hover:underline dark:text-teal-400"
                        >
                          <span dir="ltr">{supportPhone}</span>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Mail className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Email</p>
                        <a
                          href={getMailHref(supportEmail)}
                          className="text-teal-600 transition-colors hover:text-teal-700 hover:underline dark:text-teal-400"
                        >
                          <span dir="ltr">{supportEmail}</span>
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <Clock className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Working Hours</p>
                        <p className="text-slate-600 dark:text-slate-400">
                          {settings?.working_hours || 'Sat - Thu: 9AM - 9PM'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-full bg-teal-100">
                        <MapPin className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Address</p>
                        <a
                          href={getMapHref(supportAddress)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-600 transition-colors hover:text-teal-600 hover:underline dark:text-slate-400 dark:hover:text-teal-400"
                        >
                          {supportAddress}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HelpPage;
